import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, LinearProgress, Alert, Menu, MenuItem, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Breadcrumbs, Link, useMediaQuery, useTheme, ToggleButton, ToggleButtonGroup,
  Card, CardContent, CardActions, Grid, InputAdornment, FormControl, Select,
  InputLabel, Checkbox, Fade
} from '@mui/material';
import {
  Upload as UploadIcon, Download as DownloadIcon, Delete as DeleteIcon,
  Folder as FolderIcon, InsertDriveFile as FileIcon, MoreVert as MoreVertIcon,
  CreateNewFolder as CreateFolderIcon, NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon, Edit as EditIcon, ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon, ViewComfy as ViewComfyIcon, Image as ImageIcon,
  Search as SearchIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  DriveFileMove as MoveIcon, DeleteSweep as TrashIcon, Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon, Scanner as ScannerIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import ImageLightbox from './ImageLightbox';
import FolderPickerDialog from './FolderPickerDialog';
import { DocumentScannerDialog } from '../DocumentScanner';
import { downloadFile } from '../../utils/downloadUtils';
import {
  getFamilyDocuments, formatFileSize,
  createFamilyDocumentFolder, uploadToFamilyDocumentsWithPath,
  renameFamilyDocument, moveFamilyDocument, copyFamilyDocumentToPersonal,
  uploadToFamilyVaultWithPath, isFamilyAdmin
} from '../../services/familyDocumentService';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref as storageRef, deleteObject, getBlob } from 'firebase/storage';

const extractStoragePathFromUrl = (url) => {
  try {
    const m = url && url.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

const getFileTypeLabel = (doc) => {
  const fileName = doc.fileName || doc.title || doc.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && extension !== fileName.toLowerCase()) {
    return extension.toUpperCase();
  }

  const mimeType = doc.fileType || doc.mimeType || '';
  const mimeMap = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'text/plain': 'Text',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/svg+xml': 'SVG'
  };

  if (mimeMap[mimeType]) return mimeMap[mimeType];

  const shortType = mimeType.split('/')[1];
  return shortType && shortType.length < 10 ? shortType.toUpperCase() : 'File';
};

const DEFAULT_DOC_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'legal', label: 'Legal' },
  { value: 'financial', label: 'Financial' },
  { value: 'health', label: 'Health' },
  { value: 'education', label: 'Education' },
  { value: 'other', label: 'Other' }
];

const DEFAULT_PHOTO_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'family-events', label: 'Family Events' },
  { value: 'vacations', label: 'Vacations' },
  { value: 'holidays', label: 'Holidays' },
  { value: 'celebrations', label: 'Celebrations' },
  { value: 'portraits', label: 'Portraits' },
  { value: 'other', label: 'Other' }
];

const FamilyDocumentsTab = ({
  fileTypeFilter = null,
  defaultView = 'list',
  title = 'Family Documents',
  uploadButtonText = 'Upload',
  emptyMessage = 'No family documents yet. Admins can upload documents for the family.',
  storageType: storageTypeProp = null,
  acceptFileTypes = null,
  dialogTitle = null,
  categoryOptions = null
} = {}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const storageType = storageTypeProp || (fileTypeFilter === 'image/*' ? 'photos' : 'documents');
  const categories = categoryOptions || (storageType === 'photos' ? DEFAULT_PHOTO_CATEGORIES : DEFAULT_DOC_CATEGORIES);
  const uploadDialogTitle = dialogTitle || (storageType === 'photos' ? 'Upload Family Photos' : 'Upload to Family Documents');

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser && profile?.familyCode) {
        const adminStatus = await isFamilyAdmin(profile.familyCode, currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdmin();
  }, [currentUser, profile]);

  // Core state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const storageKey = fileTypeFilter === 'image/*' ? 'familyPhotosViewMode' : 'familyDocsViewMode';
    return localStorage.getItem(storageKey) || defaultView;
  });

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [uploadCategory, setUploadCategory] = useState(categories[0]?.value || 'general');

  // Trash view
  const [viewingTrash, setViewingTrash] = useState(false);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Search and sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Multi-select
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!currentUser || !profile?.familyCode) return;

    try {
      setLoading(true);
      setError(null);

      const allDocs = await getFamilyDocuments(profile.familyCode, storageType);
      setDocuments(allDocs);
    } catch (err) {
      logger.error('Error loading family documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [currentUser, profile, storageType]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const getVisibleDocuments = () => {
    let docs = documents;

    if (viewingTrash) {
      docs = docs.filter(d => d.trashed === true);
    } else {
      docs = docs.filter(d => !d.trashed);
      docs = docs.filter(d => {
        const docFolder = d.folderPath || '/';
        const current = currentPath || '/';
        return docFolder === current;
      });
    }

    return docs;
  };

  const getBreadcrumbs = () => {
    return currentPath.split('/').filter(Boolean);
  };

  const handleBreadcrumbClick = (index) => {
    const segments = getBreadcrumbs();
    const newPath = '/' + segments.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  const isImage = (doc) => {
    const mimeType = doc.fileType || doc.mimeType || '';
    return mimeType.startsWith('image/');
  };

  const getFilteredDocuments = () => {
    const docs = getVisibleDocuments();
    if (viewMode === 'gallery') {
      return docs.filter(d => d.type === 'folder' || isImage(d));
    }
    return docs;
  };

  const applySearch = (docs) => {
    if (!searchQuery.trim()) return docs;
    const query = searchQuery.toLowerCase();
    return docs.filter(d => {
      const name = (d.fileName || d.title || d.name || '').toLowerCase();
      return name.includes(query);
    });
  };

  const applySorting = (docs) => {
    const sorted = [...docs].sort((a, b) => {
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      let comparison = 0;
      switch (sortBy) {
        case 'name': {
          const nameA = (a.fileName || a.title || a.name || '').toLowerCase();
          const nameB = (b.fileName || b.title || b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'date': {
          const dateA = a.uploadedAt?.toMillis?.() || a.uploadedAt?.seconds * 1000 || 0;
          const dateB = b.uploadedAt?.toMillis?.() || b.uploadedAt?.seconds * 1000 || 0;
          comparison = dateA - dateB;
          break;
        }
        case 'size': {
          const sizeA = a.fileSize || 0;
          const sizeB = b.fileSize || 0;
          comparison = sizeA - sizeB;
          break;
        }
        case 'type': {
          const typeA = getFileTypeLabel(a);
          const typeB = getFileTypeLabel(b);
          comparison = typeA.localeCompare(typeB);
          break;
        }
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  const getProcessedDocuments = () => {
    let docs = getFilteredDocuments();
    docs = applySearch(docs);
    docs = applySorting(docs);
    return docs;
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleViewModeChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
      const storageKey = fileTypeFilter === 'image/*' ? 'familyPhotosViewMode' : 'familyDocsViewMode';
      localStorage.setItem(storageKey, newView);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleUpload(files);
    }
  };

  // Upload
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploaderName = profile?.displayName || (profile?.firstName + ' ' + profile?.lastName) || currentUser.email;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const metadata = {
          category: uploadCategory,
          storageType,
          uploadedBy: currentUser.uid,
          uploadedByName: uploaderName
        };

        await uploadToFamilyDocumentsWithPath(profile.familyCode, file, metadata, currentPath);

        const fileProgress = ((i + 1) / files.length) * 100;
        setUploadProgress(Math.round(fileProgress));
      }

      setSnackbar({
        open: true,
        message: `Successfully uploaded ${files.length} file(s)`,
        severity: 'success'
      });

      await loadDocuments();
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadCategory(categories[0]?.value || 'general');
    } catch (err) {
      logger.error('Error uploading files:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to upload files',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Scan complete
  const handleScanComplete = useCallback((result) => {
    logger.log('Scan completed:', result);
    setScannerDialogOpen(false);
    loadDocuments();
    setSnackbar({
      open: true,
      message: 'Document scanned and saved successfully!',
      severity: 'success'
    });
  }, [loadDocuments]);

  // Download
  const handleDownload = async (document) => {
    const filename = document.fileName || document.title || document.name || 'download';
    const url = document.downloadURL || document.fileUrl;
    const storagePath = document.storagePath || extractStoragePathFromUrl(url);
    setSnackbar({ open: true, message: `Downloading ${filename}…`, severity: 'info' });
    await downloadFile(url, filename, storagePath);
    setSnackbar({ open: true, message: `"${filename}" downloaded`, severity: 'success' });
  };

  // Soft delete (trash)
  const handleDelete = async () => {
    if (!selectedFile) return;

    try {
      const docRef = doc(db, 'families', profile.familyCode, 'familyDocuments', selectedFile.id);
      await updateDoc(docRef, {
        trashed: true,
        trashedAt: serverTimestamp()
      });

      setSnackbar({
        open: true,
        message: `Moved "${selectedFile.fileName}" to trash`,
        severity: 'success'
      });

      await loadDocuments();
      setDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error trashing document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move to trash',
        severity: 'error'
      });
    }
  };

  // Restore from trash
  const handleRestore = async () => {
    if (!selectedFile) return;

    try {
      const docRef = doc(db, 'families', profile.familyCode, 'familyDocuments', selectedFile.id);
      await updateDoc(docRef, {
        trashed: false,
        trashedAt: null
      });

      setSnackbar({
        open: true,
        message: `Restored "${selectedFile.fileName}"`,
        severity: 'success'
      });

      await loadDocuments();
      setRestoreDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error restoring document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to restore',
        severity: 'error'
      });
    }
  };

  // Permanent delete
  const handlePermanentDelete = async () => {
    if (!selectedFile) return;

    try {
      if (selectedFile.filePath && selectedFile.type !== 'folder') {
        try {
          const fileRef = storageRef(storage, selectedFile.filePath);
          await deleteObject(fileRef);
        } catch (storageErr) {
          logger.error('Error deleting from storage (may not exist):', storageErr);
        }
      }

      const docRef = doc(db, 'families', profile.familyCode, 'familyDocuments', selectedFile.id);
      await deleteDoc(docRef);

      setSnackbar({
        open: true,
        message: `Permanently deleted "${selectedFile.fileName}"`,
        severity: 'success'
      });

      await loadDocuments();
      setPermanentDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error permanently deleting:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to permanently delete',
        severity: 'error'
      });
    }
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const uploaderName = profile?.displayName || (profile?.firstName + ' ' + profile?.lastName) || currentUser.email;

      await createFamilyDocumentFolder(
        profile.familyCode,
        newFolderName,
        currentPath,
        currentUser.uid,
        uploaderName,
        storageType
      );

      setSnackbar({
        open: true,
        message: `Created folder "${newFolderName}"`,
        severity: 'success'
      });

      await loadDocuments();
      setFolderDialogOpen(false);
      setNewFolderName('');
    } catch (err) {
      logger.error('Error creating folder:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create folder',
        severity: 'error'
      });
    }
  };

  // Navigate into folder
  const handleFolderClick = (folder) => {
    const folderName = folder.fileName || folder.title || folder.name;
    const newPath = currentPath === '/' || currentPath === ''
      ? `/${folderName}`
      : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
  };

  // Rename
  const handleRename = async () => {
    if (!renameValue.trim() || !selectedFile) return;

    try {
      await renameFamilyDocument(profile.familyCode, selectedFile.id, renameValue);

      setSnackbar({
        open: true,
        message: `Renamed to "${renameValue}"`,
        severity: 'success'
      });

      await loadDocuments();
      setRenameDialogOpen(false);
      setRenameValue('');
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error renaming:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to rename',
        severity: 'error'
      });
    }
  };

  // Move
  const handleMove = async (destinationPath) => {
    if (!selectedFile) return;

    try {
      await moveFamilyDocument(profile.familyCode, selectedFile.id, destinationPath);

      const destDisplay = destinationPath === '/' ? 'Root' : destinationPath;
      setSnackbar({
        open: true,
        message: `Moved "${selectedFile.fileName}" to ${destDisplay}`,
        severity: 'success'
      });

      await loadDocuments();
      setMoveDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error moving:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move',
        severity: 'error'
      });
    }
  };

  // Copy to personal
  const handleCopyToPersonal = async (fileDoc) => {
    if (!fileDoc || fileDoc.type === 'folder') {
      setSnackbar({
        open: true,
        message: 'Only files can be copied to personal documents',
        severity: 'warning'
      });
      return;
    }

    try {
      await copyFamilyDocumentToPersonal(profile.familyCode, fileDoc.id, currentUser.uid);

      setSnackbar({
        open: true,
        message: `"${fileDoc.fileName}" copied to your personal documents`,
        severity: 'success'
      });
    } catch (err) {
      logger.error('Error copying to personal:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to copy to personal documents',
        severity: 'error'
      });
    }
  };

  // Copy to Family Vault (admin-only, keeps original)
  const handleCopyToFamilyVault = async (fileDoc) => {
    if (!fileDoc || fileDoc.type === 'folder') {
      setSnackbar({ open: true, message: 'Only files can be copied to Family Vault', severity: 'warning' });
      return;
    }

    try {
      const path = fileDoc.storagePath || extractStoragePathFromUrl(fileDoc.fileUrl || fileDoc.downloadURL);
      if (!path) throw new Error('Cannot resolve storage path for this file');
      const blob = await getBlob(storageRef(storage, path));
      const fileObj = new File([blob], fileDoc.fileName || fileDoc.name || fileDoc.title, { type: fileDoc.fileType || fileDoc.mimeType || blob.type });

      const uploaderName = profile?.displayName || (profile?.firstName + ' ' + profile?.lastName) || currentUser.email;
      await uploadToFamilyVaultWithPath(profile.familyCode, fileObj, {
        category: fileDoc.category || 'general',
        storageType,
        uploadedBy: currentUser.uid,
        uploadedByName: uploaderName
      }, '/');

      setSnackbar({ open: true, message: `"${fileDoc.fileName}" copied to Family Vault`, severity: 'success' });
    } catch (err) {
      logger.error('Error copying to family vault:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to copy to Family Vault', severity: 'error' });
    }
  };

  // Move to Family Vault (admin-only, removes from Family Documents)
  const handleMoveToFamilyVault = async (fileDoc) => {
    if (!fileDoc || fileDoc.type === 'folder') {
      setSnackbar({ open: true, message: 'Only files can be moved to Family Vault', severity: 'warning' });
      return;
    }

    try {
      const path = fileDoc.storagePath || extractStoragePathFromUrl(fileDoc.fileUrl || fileDoc.downloadURL);
      if (!path) throw new Error('Cannot resolve storage path for this file');
      const blob = await getBlob(storageRef(storage, path));
      const fileObj = new File([blob], fileDoc.fileName || fileDoc.name || fileDoc.title, { type: fileDoc.fileType || fileDoc.mimeType || blob.type });

      const uploaderName = profile?.displayName || (profile?.firstName + ' ' + profile?.lastName) || currentUser.email;
      await uploadToFamilyVaultWithPath(profile.familyCode, fileObj, {
        category: fileDoc.category || 'general',
        storageType,
        uploadedBy: currentUser.uid,
        uploadedByName: uploaderName
      }, '/');

      // Delete original from family documents
      const docRef = doc(db, 'families', profile.familyCode, 'familyDocuments', fileDoc.id);
      await deleteDoc(docRef);

      setSnackbar({ open: true, message: `"${fileDoc.fileName}" moved to Family Vault`, severity: 'success' });
      await loadDocuments();
    } catch (err) {
      logger.error('Error moving to family vault:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to move to Family Vault', severity: 'error' });
    }
  };

  // Selection
  const toggleItemSelection = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i.id === item.id);
      if (isSelected) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
    if (!selectionMode) {
      setSelectionMode(true);
    }
  };

  const toggleSelectAll = () => {
    const currentDocs = getProcessedDocuments();
    if (selectedItems.length === currentDocs.length) {
      setSelectedItems([]);
      setSelectionMode(false);
    } else {
      setSelectedItems(currentDocs);
      setSelectionMode(true);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectionMode(false);
  };

  // Batch operations
  const handleBatchDelete = async () => {
    if (selectedItems.length === 0) return;

    const confirmMessage = `Are you sure you want to move ${selectedItems.length} item(s) to trash?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await Promise.all(
        selectedItems.map(item => {
          const docRef = doc(db, 'families', profile.familyCode, 'familyDocuments', item.id);
          return updateDoc(docRef, { trashed: true, trashedAt: serverTimestamp() });
        })
      );

      setSnackbar({
        open: true,
        message: `Moved ${selectedItems.length} item(s) to trash`,
        severity: 'success'
      });

      clearSelection();
      await loadDocuments();
    } catch (err) {
      logger.error('Error batch deleting:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete some items',
        severity: 'error'
      });
    }
  };

  const handleBatchMove = (destinationPath) => {
    if (selectedItems.length === 0) return;

    const moveItems = async () => {
      try {
        await Promise.all(
          selectedItems.map(item =>
            moveFamilyDocument(profile.familyCode, item.id, destinationPath)
          )
        );

        const destDisplay = destinationPath === '/' ? 'Root' : destinationPath;
        setSnackbar({
          open: true,
          message: `Moved ${selectedItems.length} item(s) to ${destDisplay}`,
          severity: 'success'
        });

        clearSelection();
        await loadDocuments();
        setMoveDialogOpen(false);
      } catch (err) {
        logger.error('Error batch moving:', err);
        setSnackbar({
          open: true,
          message: err.message || 'Failed to move some items',
          severity: 'error'
        });
      }
    };

    moveItems();
  };

  const handleBatchDownload = () => {
    if (selectedItems.length === 0) return;

    const files = selectedItems.filter(item => item.type !== 'folder');
    if (files.length === 0) {
      setSnackbar({
        open: true,
        message: 'No files selected to download (folders cannot be downloaded)',
        severity: 'warning'
      });
      return;
    }

    files.forEach(file => {
      const url = file.downloadURL || file.fileUrl;
      if (url) {
        window.open(url, '_blank');
      }
    });

    setSnackbar({
      open: true,
      message: `Downloading ${files.length} file(s)`,
      severity: 'success'
    });
  };

  // Context menu
  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  // Image lightbox
  const handleImageClick = (clickedDoc) => {
    const currentDocs = getProcessedDocuments().filter(d => d.type !== 'folder' && isImage(d));
    const index = currentDocs.findIndex(d => d.id === clickedDoc.id);

    if (index !== -1) {
      setLightboxImages(currentDocs);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to access family documents.</Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please join a family to access family documents.</Alert>
      </Box>
    );
  }

  const processedDocs = getProcessedDocuments();

  return (
    <Box
      sx={{ p: 3, position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>

          {/* Breadcrumb Navigation */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="folder breadcrumb navigation"
            sx={{ mt: 1 }}
          >
            {viewingTrash ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                <TrashIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  Trash
                </Typography>
              </Box>
            ) : [
              currentPath === '/' ? (
                <Typography
                  key="root"
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main',
                    fontWeight: 600
                  }}
                >
                  <FolderIcon fontSize="small" />
                  {title}
                </Typography>
              ) : (
                <Link
                  key="root"
                  component="button"
                  variant="body2"
                  onClick={() => setCurrentPath('/')}
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.primary',
                    fontWeight: 400,
                    cursor: 'pointer'
                  }}
                >
                  <FolderIcon fontSize="small" />
                  {title}
                </Link>
              ),
              ...getBreadcrumbs().map((segment, index) => {
                const isLast = index === getBreadcrumbs().length - 1;
                return isLast ? (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 600 }}
                  >
                    {segment}
                  </Typography>
                ) : (
                  <Link
                    key={index}
                    component="button"
                    variant="body2"
                    onClick={() => handleBreadcrumbClick(index)}
                    underline="hover"
                    sx={{ color: 'text.primary', fontWeight: 400, cursor: 'pointer' }}
                  >
                    {segment}
                  </Link>
                );
              })
            ]}
          </Breadcrumbs>
        </Box>

        {/* Toolbar */}
        {selectedItems.length > 0 ? (
          <Fade in={selectedItems.length > 0}>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              px: 2,
              py: 1,
              borderRadius: 1
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 600 }}>
                {selectedItems.length} selected
              </Typography>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ color: 'inherit', borderColor: 'currentColor' }}
                  startIcon={<MoveIcon />}
                  onClick={() => setMoveDialogOpen(true)}
                >
                  Move
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                sx={{ color: 'inherit', borderColor: 'currentColor' }}
                startIcon={<DownloadIcon />}
                onClick={handleBatchDownload}
              >
                Download
              </Button>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ color: 'inherit', borderColor: 'currentColor' }}
                  startIcon={<DeleteIcon />}
                  onClick={handleBatchDelete}
                >
                  Delete
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                sx={{ color: 'inherit', ml: 'auto' }}
                onClick={clearSelection}
              >
                Clear
              </Button>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
            {!viewingTrash && isAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateFolderIcon />}
                  onClick={() => setFolderDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  {uploadButtonText}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ScannerIcon />}
                  onClick={() => setScannerDialogOpen(true)}
                  size="small"
                  fullWidth={isMobile}
                >
                  Scan
                </Button>
              </>
            )}
            <Button
              variant={viewingTrash ? 'contained' : 'outlined'}
              startIcon={<TrashIcon />}
              onClick={() => {
                setViewingTrash(!viewingTrash);
                setCurrentPath('/');
                setSelectedItems([]);
                setSelectionMode(false);
              }}
              size="small"
              fullWidth={isMobile}
              color={viewingTrash ? 'primary' : 'inherit'}
            >
              {viewingTrash ? 'Back' : 'Trash'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Search, Sort, View Controls */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between'
      }}>
        <TextField
          size="small"
          placeholder="Search files and folders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: isMobile ? '100%' : 250,
            maxWidth: isMobile ? '100%' : 400
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="family-sort-by-label">Sort by</InputLabel>
            <Select
              labelId="family-sort-by-label"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            size="small"
            onClick={toggleSortOrder}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="gallery" aria-label="gallery view">
              <ViewComfyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Empty state */}
      {!loading && processedDocs.length === 0 && (
        <Alert severity="info">
          {viewingTrash
            ? 'Trash is empty.'
            : searchQuery
              ? 'No files match your search.'
              : emptyMessage
          }
        </Alert>
      )}

      {/* Documents Display */}
      {!loading && processedDocs.length > 0 && (() => {
        if (processedDocs.length === 0 && viewMode === 'gallery') {
          return (
            <Alert severity="info" sx={{ mt: 2 }}>
              No images in this folder. Switch to List or Grid view to see all files.
            </Alert>
          );
        }

        // LIST VIEW
        if (viewMode === 'list') {
          return (
            <TableContainer component={Paper} sx={{
              mt: 2,
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: isMobile ? 500 : 750
              }
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < processedDocs.length}
                        checked={processedDocs.length > 0 && selectedItems.length === processedDocs.length}
                        onChange={toggleSelectAll}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedDocs.map((docItem) => {
                    const isSelected = selectedItems.some(item => item.id === docItem.id);
                    return (
                      <TableRow
                        key={docItem.id}
                        hover
                        selected={isSelected}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleItemSelection(docItem);
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          onClick={() => {
                            if (docItem.type === 'folder') {
                              handleFolderClick(docItem);
                            } else if (isImage(docItem)) {
                              handleImageClick(docItem);
                            } else {
                              handleDownload(docItem);
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {docItem.type === 'folder' ? (
                              <FolderIcon color="primary" fontSize="small" />
                            ) : isImage(docItem) && docItem.downloadURL ? (
                              <Box
                                component="img"
                                src={docItem.downloadURL}
                                alt=""
                                sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 0.5 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <FileIcon color="action" fontSize="small" />
                            )}
                            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                              {docItem.fileName || docItem.title || 'Untitled'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {docItem.type === 'folder' ? 'Folder' : getFileTypeLabel(docItem)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {docItem.type === 'folder' ? '-' : formatFileSize(docItem.fileSize || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                            {docItem.uploadedByName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(docItem.uploadedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, docItem);
                            }}
                          >
                            <MoreVertIcon fontSize={isMobile ? 'medium' : 'small'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }

        // GRID VIEW
        if (viewMode === 'grid') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {processedDocs.map((docItem) => {
                const isSelected = selectedItems.some(item => item.id === docItem.id);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={docItem.id}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        border: isSelected ? 2 : 0,
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        '&:hover': { boxShadow: 4 }
                      }}
                      onClick={() => {
                        if (docItem.type === 'folder') {
                          handleFolderClick(docItem);
                        } else if (isImage(docItem)) {
                          handleImageClick(docItem);
                        } else {
                          handleDownload(docItem);
                        }
                      }}
                    >
                      <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleItemSelection(docItem);
                          }}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.8)',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                          }}
                        />
                      </Box>
                      <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                        <Box sx={{ mb: 2, mt: 3 }}>
                          {docItem.type === 'folder' ? (
                            <FolderIcon color="primary" sx={{ fontSize: 64 }} />
                          ) : isImage(docItem) && docItem.downloadURL ? (
                            <Box
                              component="img"
                              src={docItem.downloadURL}
                              alt={docItem.fileName || 'Image'}
                              sx={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 1
                              }}
                            />
                          ) : (
                            <FileIcon color="action" sx={{ fontSize: 64 }} />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          title={docItem.fileName}
                        >
                          {docItem.fileName || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {docItem.type === 'folder' ? 'Folder' : formatFileSize(docItem.fileSize || 0)}
                        </Typography>
                        {docItem.uploadedByName && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {docItem.uploadedByName}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, docItem);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        }

        // GALLERY VIEW
        if (viewMode === 'gallery') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {processedDocs.map((docItem) => {
                const isSelected = selectedItems.some(item => item.id === docItem.id);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={docItem.id}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        border: isSelected ? 2 : 0,
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        '&:hover': { boxShadow: 6 }
                      }}
                      onClick={() => {
                        if (docItem.type === 'folder') {
                          handleFolderClick(docItem);
                        } else {
                          handleImageClick(docItem);
                        }
                      }}
                    >
                      <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleItemSelection(docItem);
                          }}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.8)',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                          }}
                        />
                      </Box>
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        {docItem.type === 'folder' ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <FolderIcon color="primary" sx={{ fontSize: 80 }} />
                            <Typography variant="body2" fontWeight={500} sx={{ mt: 1, px: 2 }}>
                              {docItem.fileName || 'Folder'}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Box
                              component="img"
                              src={docItem.downloadURL || ''}
                              alt={docItem.fileName || 'Image'}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <Box
                              sx={{
                                display: 'none',
                                width: '100%',
                                height: 200,
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'action.hover'
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                            </Box>
                            <Box sx={{ p: 1 }}>
                              <Typography variant="caption" fontWeight={500} noWrap>
                                {docItem.fileName || 'Image'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatFileSize(docItem.fileSize || 0)}
                              </Typography>
                              {docItem.uploadedByName && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {docItem.uploadedByName}
                                </Typography>
                              )}
                            </Box>
                          </>
                        )}
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, docItem);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        }

        return null;
      })()}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {viewingTrash ? (
          [
            <MenuItem key="restore" onClick={() => {
              setSelectedFile(menuItem);
              setRestoreDialogOpen(true);
              handleMenuClose();
            }}>
              <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
              Restore
            </MenuItem>,
            <MenuItem key="delete-forever" onClick={() => {
              setSelectedFile(menuItem);
              setPermanentDeleteDialogOpen(true);
              handleMenuClose();
            }}>
              <DeleteForeverIcon sx={{ mr: 1 }} fontSize="small" color="error" />
              Delete Forever
            </MenuItem>
          ]
        ) : (
          [
            menuItem?.type !== 'folder' && (
              <MenuItem key="download" onClick={() => {
                handleDownload(menuItem);
                handleMenuClose();
              }}>
                <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
                Download
              </MenuItem>
            ),
            menuItem?.type !== 'folder' && (
              <MenuItem key="copy-personal" onClick={() => {
                handleCopyToPersonal(menuItem);
                handleMenuClose();
              }}>
                <CopyIcon sx={{ mr: 1 }} fontSize="small" color="primary" />
                Copy to Personal
              </MenuItem>
            ),
            isAdmin && menuItem?.type !== 'folder' && (
              <MenuItem key="copy-family-vault" onClick={() => {
                handleCopyToFamilyVault(menuItem);
                handleMenuClose();
              }}>
                <CopyIcon sx={{ mr: 1 }} fontSize="small" color="secondary" />
                Copy to Family Vault
              </MenuItem>
            ),
            isAdmin && menuItem?.type !== 'folder' && (
              <MenuItem key="move-family-vault" onClick={() => {
                handleMoveToFamilyVault(menuItem);
                handleMenuClose();
              }}>
                <MoveIcon sx={{ mr: 1 }} fontSize="small" color="secondary" />
                Move to Family Vault
              </MenuItem>
            ),
            isAdmin && (
              <MenuItem key="rename" onClick={() => {
                setSelectedFile(menuItem);
                setRenameValue(menuItem?.fileName || '');
                setRenameDialogOpen(true);
                handleMenuClose();
              }}>
                <EditIcon sx={{ mr: 1 }} fontSize="small" />
                Rename
              </MenuItem>
            ),
            isAdmin && (
              <MenuItem key="move" onClick={() => {
                setSelectedFile(menuItem);
                setMoveDialogOpen(true);
                handleMenuClose();
              }}>
                <MoveIcon sx={{ mr: 1 }} fontSize="small" />
                Move to Folder
              </MenuItem>
            ),
            isAdmin && (
              <MenuItem key="delete" onClick={() => {
                setSelectedFile(menuItem);
                setDeleteDialogOpen(true);
                handleMenuClose();
              }}>
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                Move to Trash
              </MenuItem>
            )
          ].filter(Boolean)
        )}
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{uploadDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select files to upload to the family {storageType === 'photos' ? 'photos' : 'documents'} space.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 100MB
          </Typography>
          {currentPath !== '/' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Uploading to: {currentPath}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              multiple
              accept={acceptFileTypes || (fileTypeFilter === 'image/*' ? 'image/*' : undefined)}
              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
              style={{ width: '100%' }}
            />
          </Box>
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                Selected files ({selectedFiles.length}):
              </Typography>
              {selectedFiles.map((file, index) => (
                <Typography key={index} variant="caption" display="block">
                  {file.name} ({formatFileSize(file.size)})
                </Typography>
              ))}
            </Box>
          )}
          <TextField
            select
            fullWidth
            label="Category"
            value={uploadCategory}
            onChange={(e) => setUploadCategory(e.target.value)}
            disabled={uploading}
            sx={{ mt: 2 }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleUpload(selectedFiles)}
            disabled={uploading || selectedFiles.length === 0}
            variant="contained"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (Soft Delete / Trash) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Move to Trash?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move &quot;{selectedFile?.fileName}&quot; to trash?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can restore this item from the trash later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="warning" variant="contained">
            Move to Trash
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Restore from Trash?</DialogTitle>
        <DialogContent>
          <Typography>
            Restore &quot;{selectedFile?.fileName}&quot; to its original location?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The item will be moved back to the family documents.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRestore} color="primary" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => setPermanentDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Permanently Delete?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Warning: This action cannot be undone!
            </Typography>
          </Alert>
          <Typography>
            Are you sure you want to permanently delete &quot;{selectedFile?.fileName}&quot;?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This will delete the file from storage permanently. You cannot restore it after deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained">
            Delete Forever
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          {currentPath !== '/' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Creating inside: {currentPath}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Rename {selectedFile?.type === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameValue.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag & Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" color="primary" gutterBottom>
              Drop files here to upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload to: {currentPath === '/' ? 'Root' : currentPath}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />

      {/* Folder Picker Dialog for Move */}
      <FolderPickerDialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setSelectedFile(null);
        }}
        onSelect={selectedItems.length > 0 ? handleBatchMove : handleMove}
        allDocuments={documents.filter(d => !d.trashed)}
        currentPath={selectedFile?.folderPath || '/'}
        title={selectedItems.length > 0 ? `Move ${selectedItems.length} item(s) to Folder` : 'Move to Folder'}
      />

      {/* Document Scanner Dialog */}
      <DocumentScannerDialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        onComplete={handleScanComplete}
        destinationPath={currentPath}
        destinationType="family"
        storageStrategy="documentService"
      />
    </Box>
  );
};

export default FamilyDocumentsTab;
